import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import type { ExtractedItemData } from '../hooks/use-item-extraction';

interface ExtractedDataContextType {
  extractedData: ExtractedItemData | null;
  setExtractedData: (data: ExtractedItemData | null) => void;
  /**
   * Timestamp of the last successful AI extraction. Consumers use this to
   * surface non-blocking warnings on required fields the IA couldn't fill,
   * without flagging missing fields in plain manual entry mode.
   */
  extractionCompletedAt: number | null;
  consumeFields: <K extends keyof ExtractedItemData>(
    fields: K[],
  ) => Pick<ExtractedItemData, K> | null;
}

const ExtractedDataContext = createContext<ExtractedDataContextType | null>(null);

export function ExtractedDataProvider({ children }: { children: ReactNode }) {
  const [extractedData, setExtractedData] = useState<ExtractedItemData | null>(null);
  const [extractionCompletedAt, setExtractionCompletedAt] = useState<number | null>(null);
  const consumedFieldsRef = useRef<Set<string>>(new Set());

  const handleSetExtractedData = useCallback((data: ExtractedItemData | null) => {
    consumedFieldsRef.current.clear();
    setExtractedData(data);
    setExtractionCompletedAt(data ? Date.now() : null);
  }, []);

  const consumeFields = useCallback(
    <K extends keyof ExtractedItemData>(fields: K[]): Pick<ExtractedItemData, K> | null => {
      if (!extractedData) {
        return null;
      }

      const unconsumed = fields.filter((f) => !consumedFieldsRef.current.has(f));
      if (unconsumed.length === 0) {
        return null;
      }

      for (const field of unconsumed) {
        consumedFieldsRef.current.add(field);
      }

      const result = {} as Pick<ExtractedItemData, K>;
      for (const field of unconsumed) {
        result[field] = extractedData[field];
      }
      return result;
    },
    [extractedData],
  );

  return (
    <ExtractedDataContext.Provider
      value={{
        extractedData,
        setExtractedData: handleSetExtractedData,
        extractionCompletedAt,
        consumeFields,
      }}
    >
      {children}
    </ExtractedDataContext.Provider>
  );
}

export function useExtractedData() {
  const context = useContext(ExtractedDataContext);
  if (!context) {
    throw new Error('useExtractedData must be used within ExtractedDataProvider');
  }
  return context;
}
